import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

const MAX_ATTACHMENTS = 20

export default class extends Controller {
  static targets = [
    "sidebar",
    "expandedContent",
    "collapsedContent",
    "messages",
    "input",
    "sendButton",
    "stopButton",
    "typingIndicator",
    "conversationList",
    "conversationItems",
    "attachmentInput",
    "attachmentPreview",
    "attachmentButton",
    "voiceButton",
    "recordingIndicator",
    "liveStatus"
  ]

  static values = {
    agentType: { type: String, default: "seller" },
    contextType: String,
    contextId: Number,
    conversationId: Number,
    globalWelcome: String,
    updatingText: String,
    updatedText: String,
    refreshFailedText: String,
    messages: Object,
    collapsed: { type: Boolean, default: true }
  }

  connect() {
    this.consumer = createConsumer()
    this.currentResponse = ""
    this.currentResponseElement = null
    this.resetSequencing()
    this.activeRunId = null
    this.stoppedRunIds = new Set()
    this.runPollTimer = null
    this.pendingMessageRequest = null
    this.sequenceGapTimer = null
    this.sequenceGapDetected = false
    this.liveRefreshTimer = null

    // Attachment state
    this.pendingAttachments = [] // { file, signedId, previewUrl, contentType, filename }

    // Voice recording state
    this.mediaRecorder = null
    this.audioChunks = []
    this.isRecording = false

    // Restore collapsed state from localStorage only for the docked seller
    // sidebar. The builder's AI panel is a fixed, non-collapsible tab.
    const savedState = localStorage.getItem("ai-sidebar-collapsed")
    if (savedState !== null && this.hasCollapsedContentTarget) {
      this.collapsedValue = savedState === "true"
    }

    this.updateCollapsedState()

    // A conversation explicitly selected by the caller/history may be restored.
    // Otherwise every newly connected seller/builder panel starts fresh.
    if (this.conversationIdValue) {
      this.subscribeToConversation(this.conversationIdValue)
      this.loadHistory()
    } else {
      this.startFreshConversation()
    }
  }

  resetSequencing() {
    if (this.sequenceGapTimer) clearTimeout(this.sequenceGapTimer)
    this.sequenceGapTimer = null
    this.sequenceGapDetected = false
    this.expectedSeq = 1
    this.messageBuffer = new Map()
    // After a reconnect the job may already be at seq N. The first event that
    // reaches this controller becomes the new baseline; otherwise all later
    // events remain buffered forever waiting for broadcasts that were missed.
    this.awaitingSequenceBaseline = true
  }

  disconnect() {
    this.clearResponseTimeout()
    this.stopRunPolling()
    if (this.sequenceGapTimer) clearTimeout(this.sequenceGapTimer)
    if (this.liveRefreshTimer) clearTimeout(this.liveRefreshTimer)
    this.subscription?.unsubscribe()
    this.consumer?.disconnect()
    this.pendingAttachments.forEach((attachment) => this.revokeObjectUrl(attachment.previewUrl))
  }

  // Toggle sidebar expanded/collapsed
  toggle() {
    this.collapsedValue = !this.collapsedValue
    localStorage.setItem("ai-sidebar-collapsed", this.collapsedValue)
    this.updateCollapsedState()
  }

  startFreshConversation() {
    this.conversationIdValue = undefined
    this.subscription?.unsubscribe()
    this.subscription = null
    this.subscriptionConnected = false
    this.subscriptionEverConnected = false
    this.pendingMessageRequest = null
    this.activeRunId = null
    this.currentResponse = ""
    this.currentResponseElement = null
    this.stopRunPolling()
    this.clearResponseTimeout()
    this.resetSequencing()
    this.hideTypingIndicator()
    this.enableSend()

    if (this.hasMessagesTarget) {
      this.messagesTarget.innerHTML = ""
      if (this.welcomeMessage) this.appendMessage("assistant", this.welcomeMessage)
    }

    if (this.hasConversationListTarget) {
      this.conversationListTarget.classList.add("hidden")
    }
  }

  updateCollapsedState() {
    if (this.collapsedValue) {
      // Collapsed: show thin bar, hide expanded content
      if (this.hasExpandedContentTarget) {
        this.expandedContentTarget.classList.add("hidden")
      }
      if (this.hasCollapsedContentTarget) {
        this.collapsedContentTarget.classList.remove("hidden")
      }
    } else {
      // Expanded: show full chat, hide thin bar
      if (this.hasExpandedContentTarget) {
        this.expandedContentTarget.classList.remove("hidden")
      }
      if (this.hasCollapsedContentTarget) {
        this.collapsedContentTarget.classList.add("hidden")
      }
      // Focus input when expanded
      setTimeout(() => this.inputTarget?.focus(), 100)
    }
  }

  async createConversation() {
    try {
      const response = await fetch("/seller/ai/conversations", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({
          agent_type: this.agentTypeValue,
          context_type: this.hasPageContext ? this.contextTypeValue : null,
          context_id: this.hasPageContext ? this.contextIdValue : null,
          page_url: window.location.pathname
        })
      })

      if (!response.ok) {
        throw new Error(this.message("start_failed", "Failed to start conversation. Please try again."))
      }

      const data = await response.json()
      this.conversationIdValue = data.conversation_id
      this.subscribeToConversation(data.conversation_id)
    } catch (error) {
      console.error("Error creating conversation:", error)
      this.showError(this.message("start_failed", "Failed to start conversation. Please try again."))
    }
  }

  subscribeToConversation(conversationId) {
    this.subscription?.unsubscribe()
    this.subscriptionConnected = false
    this.subscriptionEverConnected = false

    this.subscription = this.consumer.subscriptions.create(
      { channel: "AiConversationChannel", conversation_id: conversationId },
      {
        received: (data) => this.handleMessage(data),
        connected: () => {
          const reconnected = this.subscriptionEverConnected
          this.subscriptionConnected = true
          this.subscriptionEverConnected = true

          if (reconnected) {
            this.resetSequencing()
            this.loadHistory()
            if (this.activeRunId) this.startRunPolling()
          }
        },
        disconnected: () => { this.subscriptionConnected = false },
        rejected: () => { this.subscriptionConnected = false }
      }
    )
  }

  async sendMessage(event) {
    event?.preventDefault()
    if (this.activeRunId) return

    const message = this.inputTarget.value.trim()
    const submittedAttachments = [...this.pendingAttachments]
    const attachmentSignedIds = submittedAttachments.map(a => a.signedId)

    if (!message && attachmentSignedIds.length === 0) return

    this.showTypingIndicator()
    this.disableSend()

    // Reset sequencing for new response
    this.resetSequencing()
    let optimisticMessage = null

    try {
      if (!this.conversationIdValue) await this.createConversation()
      if (!this.conversationIdValue) throw new Error(this.message("start_failed", "Failed to start conversation. Please try again."))
      optimisticMessage = this.appendMessage("user", message, submittedAttachments)
      const requestFingerprint = JSON.stringify([
        this.conversationIdValue,
        message,
        attachmentSignedIds
      ])
      if (this.pendingMessageRequest?.fingerprint !== requestFingerprint) {
        this.pendingMessageRequest = {
          fingerprint: requestFingerprint,
          id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
        }
      }

      // Commands use HTTP so the server can acknowledge a durable run_id.
      // Action Cable remains the low-latency event channel; polling is the
      // recovery path when a socket reconnects or drops a broadcast.
      const response = await fetch(`/seller/ai/conversations/${this.conversationIdValue}/messages`, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({
          request_id: this.pendingMessageRequest.id,
          message,
          attachment_signed_ids: attachmentSignedIds,
          page_url: window.location.pathname,
          context_type: this.hasPageContext ? this.contextTypeValue : null,
          context_id: this.hasPageContext ? this.contextIdValue : null
        })
      })
      const data = await response.json()
      if (!response.ok) {
        this.pendingMessageRequest = null
        throw new Error(data.error || this.message("queue_failed", "The AI request could not be queued"))
      }

      this.pendingMessageRequest = null
      this.inputTarget.value = ""
      this.resetInputHeight()
      this.clearAttachmentPreviews()
      this.activeRunId = data.run_id
      this.updateRunControls()
      this.startRunPolling()
      this.startResponseTimeout()
    } catch (error) {
      optimisticMessage?.remove()
      this.hideTypingIndicator()
      this.enableSend()
      this.showError(error.message || this.message("unreachable", "Couldn’t reach the AI service. Please try again."))
      this.loadHistory()
    }
  }

  // Safety net: if no event arrives after a send, stop the spinner so the chat
  // never hangs silently. Cleared as soon as any event is received.
  startResponseTimeout() {
    this.clearResponseTimeout()
    this.responseTimeout = setTimeout(() => {
      this.hideTypingIndicator()
      this.enableSend()
      this.showError(this.message("no_response", "The AI didn’t respond. Please try again."))
    }, 270000)
  }

  clearResponseTimeout() {
    if (this.responseTimeout) {
      clearTimeout(this.responseTimeout)
      this.responseTimeout = null
    }
  }

  handleMessage(data) {
    // A stopped run's worker may still flush a few events before it notices.
    if (data.run_id && this.stoppedRunIds.has(data.run_id)) return

    if (data.run_id && ![ "complete", "error", "request_rejected", "cancelled" ].includes(data.type)) {
      this.activeRunId = data.run_id
      this.disableSend()
      this.startRunPolling(data.type === "waiting_confirmation" ? 5000 : 2000)
    }

    // Keep a rolling watchdog alive throughout long multi-step builds.
    if ([ "complete", "error", "waiting_confirmation", "request_rejected", "cancelled" ].includes(data.type)) {
      this.clearResponseTimeout()
    } else {
      this.startResponseTimeout()
    }

    // Handle sequencing - buffer out-of-order messages
    if (data.seq) {
      if (this.awaitingSequenceBaseline) {
        this.expectedSeq = data.seq
        this.awaitingSequenceBaseline = false
      }

      if (data.seq === this.expectedSeq) {
        // Process this message and any buffered ones that follow
        this.processMessage(data)
        this.expectedSeq++

        // Process any buffered messages that are now in order
        while (this.messageBuffer.has(this.expectedSeq)) {
          const bufferedData = this.messageBuffer.get(this.expectedSeq)
          this.messageBuffer.delete(this.expectedSeq)
          this.processMessage(bufferedData)
          this.expectedSeq++
        }
      } else if (data.seq > this.expectedSeq) {
        // Buffer this message for later
        this.messageBuffer.set(data.seq, data)
        this.scheduleSequenceGapRecovery()
      }
      // Ignore messages with seq < expectedSeq (duplicates)
    } else {
      // No sequence number - process immediately (backward compatibility)
      this.processMessage(data)
    }
  }

  scheduleSequenceGapRecovery() {
    if (this.sequenceGapTimer) return

    this.sequenceGapTimer = setTimeout(() => {
      this.sequenceGapTimer = null
      if (this.messageBuffer.size === 0) return

      // WebSocket delivery is transient. Do not wait forever for a missing
      // sequence: continue from the first event we do have and let persisted
      // history/polling restore the complete final answer.
      this.sequenceGapDetected = true
      this.expectedSeq = Math.min(...this.messageBuffer.keys())
      while (this.messageBuffer.has(this.expectedSeq)) {
        const bufferedData = this.messageBuffer.get(this.expectedSeq)
        this.messageBuffer.delete(this.expectedSeq)
        this.processMessage(bufferedData)
        this.expectedSeq++
      }
      if (this.messageBuffer.size > 0) this.scheduleSequenceGapRecovery()
    }, 750)
  }

  processMessage(data) {
    switch (data.type) {
      case "content":
        this.hideTypingIndicator()
        this.appendToCurrentResponse(data.data)
        break

      case "tool_confirmation":
        // Close the current text bubble so what comes next renders below it.
        this.finalizeResponse()
        this.hideTypingIndicator()
        this.showToolConfirmation(data.data)
        break

      case "tool_result":
        // Close the current text bubble so the tool card (and any following text)
        // stay in chronological order instead of piling into one bubble.
        this.finalizeResponse()
        this.showToolResult(data.data, data.tool_name || data.tool)
        this.scheduleLiveContextRefresh(data.data)
        this.notifyBuilderPreview()
        // The agent is still working on the next step — keep the indicator up.
        this.showTypingIndicator()
        break

      case "waiting_confirmation":
        this.activeRunId = data.run_id || this.activeRunId
        this.finalizeResponse()
        this.hideTypingIndicator()
        this.disableSend()
        this.startRunPolling(5000)
        break

      case "request_rejected":
        this.finalizeResponse()
        this.hideTypingIndicator()
        this.enableSend()
        this.showError(data.data)
        break

      case "citations":
        this.applyInlineCitations(data.data)
        this.finalizeResponse()
        this.showCitations(data.data)
        break

      case "complete":
        this.stopRunPolling()
        this.activeRunId = null
        this.finalizeResponse()
        this.hideTypingIndicator()
        this.enableSend()
        this.notifyBuilderPreview()
        this.loadHistory()
        break

      case "cancelled":
        this.finishStoppedRun(data.run_id)
        break

      case "error":
        this.stopRunPolling()
        this.activeRunId = null
        this.finalizeResponse()
        this.hideTypingIndicator()
        this.showError(data.data)
        this.enableSend()
        if (this.sequenceGapDetected) this.loadHistory()
        break
    }
  }

  // In the site builder, tell the canvas to live-refresh after the AI mutates
  // the draft (blocks / header / footer / tokens) so changes show without a reload.
  notifyBuilderPreview() {
    if (!this.agentTypeValue.startsWith("builder")) return

    window.dispatchEvent(new CustomEvent("builder:preview-refresh"))
  }

  appendMessage(role, content, attachments = [], citations = []) {
    const messageHtml = this.buildMessageHtml(role, content, attachments, citations)
    this.messagesTarget.insertAdjacentHTML("beforeend", messageHtml)
    this.scrollToBottom()
    return this.messagesTarget.lastElementChild
  }

  appendToCurrentResponse(content) {
    if (!this.currentResponseElement) {
      const wrapper = document.createElement("div")
      wrapper.className = "max-w-[90%] min-w-0 mr-auto"
      this.currentResponseElement = document.createElement("div")
      this.currentResponseElement.className = "bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white rounded-2xl rounded-bl-md px-4 py-2 text-sm leading-relaxed break-words overflow-hidden"
      wrapper.appendChild(this.currentResponseElement)
      this.messagesTarget.appendChild(wrapper)
    }

    this.currentResponse += content
    this.currentResponseElement.innerHTML = this.formatMarkdown(this.currentResponse)
    this.scrollToBottom()
  }

  finalizeResponse() {
    if (this.currentResponseElement) {
      // Save final response
      this.currentResponseElement = null
      this.currentResponse = ""
    }
  }

  showToolConfirmation(data) {
    const html = `
      <div class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg p-3 my-2 min-w-0 overflow-hidden" data-execution-id="${data.execution_id}">
        <div class="flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm font-medium min-w-0">
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span class="min-w-0 break-words">${this.escapeHtml(this.message("confirm_prompt", "Agent wants to:"))} <strong>${this.escapeHtml(data.tool_name)}</strong></span>
        </div>
        <div class="mt-2 bg-white dark:bg-slate-800 rounded p-2 overflow-x-auto max-w-full">
          <pre class="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap break-words">${this.formatParams(data.params)}</pre>
        </div>
        <div class="flex gap-2 mt-3">
          <button type="button"
                  class="px-3 py-1.5 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 cursor-pointer"
                  data-action="ai-sidebar#confirmTool"
                  data-execution-id="${data.execution_id}">
            ${this.escapeHtml(this.message("confirm", "Confirm"))}
          </button>
          <button type="button"
                  class="px-3 py-1.5 text-sm font-medium rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600 cursor-pointer"
                  data-action="ai-sidebar#rejectTool"
                  data-execution-id="${data.execution_id}">
            ${this.escapeHtml(this.message("reject", "Reject"))}
          </button>
        </div>
      </div>
    `
    this.messagesTarget.insertAdjacentHTML("beforeend", html)
    this.scrollToBottom()
  }

  showCitations(citations) {
    const safeCitations = this.safeCitations(citations)
    if (safeCitations.length === 0) return

    const links = safeCitations.map((citation, index) => `
      <a href="${this.escapeHtml(citation.url)}" target="_blank" rel="noopener noreferrer"
         class="inline-flex max-w-full items-center gap-1 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-xs text-teal-700 dark:text-teal-300 hover:underline">
        <span>${index + 1}.</span>
        <span class="truncate">${this.escapeHtml(citation.title || citation.url)}</span>
      </a>`).join("")

    this.messagesTarget.insertAdjacentHTML("beforeend", `
      <div class="max-w-[90%] mr-auto flex flex-wrap gap-1" aria-label="${this.escapeHtml(this.message("web_sources", "Web sources"))}">${links}</div>`)
    this.scrollToBottom()
  }

  safeCitations(citations) {
    return (citations || []).filter((citation) => {
      try {
        const url = new URL(citation.url)
        return url.protocol === "http:" || url.protocol === "https:"
      } catch (_error) {
        return false
      }
    })
  }

  applyInlineCitations(citations) {
    if (!this.currentResponseElement || !this.currentResponse) return

    this.currentResponseElement.innerHTML = this.formatMarkdownWithCitations(this.currentResponse, citations)
  }

  async confirmTool(event) {
    const executionId = event.currentTarget.dataset.executionId
    const confirmationEl = event.currentTarget.closest("[data-execution-id]")

    try {
      // Show executing state
      confirmationEl.innerHTML = '<div class="text-slate-500 dark:text-slate-400 text-sm flex items-center gap-2"><svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>' + this.escapeHtml(this.message("executing", "Executing...")) + '</div>'

      const response = await fetch(`/seller/ai/tool_executions/${executionId}/confirm`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        }
      })

      const result = await response.json()

      if (response.ok) {
        if (result.accepted) {
          confirmationEl.innerHTML = `<div class="text-slate-500 dark:text-slate-400 text-sm">${this.escapeHtml(this.message("approved", "Approved — executing…"))}</div>`
          this.showTypingIndicator()
          this.startResponseTimeout()
          return
        }

        // Show result in place of confirmation
        const isSuccess = result.success !== false
        const bgClass = isSuccess
          ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300"
          : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300"
        const editUrl = this.safeInternalUrl(result.data?.edit_url)

        confirmationEl.className = `rounded-lg p-3 my-2 text-sm min-w-0 break-words overflow-hidden ${bgClass}`
        confirmationEl.innerHTML = `
          <p>${this.escapeHtml(result.message || result.error || this.message("action_completed", "Action completed"))}</p>
          ${editUrl ? `<a href="${editUrl}" class="text-teal-600 dark:text-teal-400 underline hover:no-underline mt-2 inline-block">${this.escapeHtml(this.message("view_edit", "View / edit"))}</a>` : ""}
        `
        this.scheduleLiveContextRefresh(result)
      } else {
        confirmationEl.className = "rounded-lg p-3 my-2 text-sm min-w-0 break-words overflow-hidden bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300"
        confirmationEl.innerHTML = `<p>${this.escapeHtml(result.error || this.message("execution_failed", "Execution failed"))}</p>`
      }
    } catch (error) {
      console.error("Error confirming tool:", error)
      confirmationEl.innerHTML = `<div class="text-red-600 dark:text-red-400 text-sm">${this.escapeHtml(this.message("execution_error", "Error executing tool"))}</div>`
    }
  }

  async rejectTool(event) {
    const executionId = event.currentTarget.dataset.executionId
    const confirmationEl = event.currentTarget.closest("[data-execution-id]")

    try {
      const response = await fetch(`/seller/ai/tool_executions/${executionId}/reject`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken
        }
      })

      if (response.ok) {
        confirmationEl.innerHTML = `<div class="text-red-600 dark:text-red-400 text-sm">${this.escapeHtml(this.message("rejected", "Rejected"))}</div>`
      }
    } catch (error) {
      console.error("Error rejecting tool:", error)
    }
  }

  // Friendly label + category per tool so the chat reads like a build log:
  // inspection steps are quiet, real changes get a clear ✓/✗ action row.
  toolMeta(key) {
    const map = {
      builder_reset: { label: "Cleared the draft", kind: "write" },
      builder_update_tokens: { label: "Applied theme", kind: "write" },
      builder_set_page_tree: { label: "Built layout", kind: "write" },
      builder_create_page: { label: "Created a page", kind: "write" },
      builder_update_page_meta: { label: "Updated page settings", kind: "write" },
      builder_apply_block_patch: { label: "Edited blocks", kind: "write" },
      builder_bind_data: { label: "Connected live data", kind: "write" },
      builder_search_unsplash: { label: "Found images", kind: "read" },
      builder_publish: { label: "Published the site", kind: "write" },
      builder_list_pages: { label: "Checked pages", kind: "read" },
      builder_get_page: { label: "Read the page", kind: "read" },
      builder_list_blocks: { label: "Checked blocks", kind: "read" },
      list_products: { label: "Checked products", kind: "read" },
      get_product: { label: "Read product details", kind: "read" },
      list_orders: { label: "Checked orders", kind: "read" },
      get_order: { label: "Read order details", kind: "read" },
      list_supplies: { label: "Checked supplies", kind: "read" },
      get_supply: { label: "Read supply details", kind: "read" },
      list_categories: { label: "Checked categories", kind: "read" },
      list_attributes: { label: "Checked attributes", kind: "read" },
      list_reference_groups: { label: "Checked reference groups", kind: "read" },
      list_reference_items: { label: "Checked reference items", kind: "read" },
      create_product: { label: "Created product", kind: "write" },
      update_product: { label: "Updated product", kind: "write" },
      create_variant: { label: "Added product variant", kind: "write" },
      update_variant: { label: "Updated product variant", kind: "write" },
      attach_product_images: { label: "Added product images", kind: "write" },
      upsert_product_references: { label: "Linked product references", kind: "write" },
      create_order: { label: "Created order", kind: "write" },
      update_order: { label: "Updated order", kind: "write" },
      create_supply: { label: "Created supply", kind: "write" },
      update_supply: { label: "Updated supply", kind: "write" }
    }
    const meta = map[key] || { label: this.formatToolName(key), kind: "write" }
    return { ...meta, label: this.message(`tools.${key}`, meta.label) }
  }

  // Copy translated on the server (seller.ai.sidebar.js); falls back to the
  // English text when a key is missing. %{name} placeholders take vars.
  message(key, fallback, vars = {}) {
    const text = key.split(".").reduce((node, part) => node?.[part], this.hasMessagesValue ? this.messagesValue : null)
    return String(typeof text === "string" ? text : fallback).replace(/%\{(\w+)\}/g, (_, name) => vars[name] ?? "")
  }

  escapeHtml(value) {
    return String(value ?? "").replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ))
  }

  safeInternalUrl(value) {
    const raw = String(value ?? "").trim()
    if (!raw.startsWith("/") || raw.startsWith("//") || raw.includes("\\") || /[\u0000-\u001F\u007F]/.test(raw)) return null

    try {
      const url = new URL(raw, window.location.origin)
      if (url.origin !== window.location.origin) return null

      return this.escapeHtml(`${url.pathname}${url.search}${url.hash}`)
    } catch (_error) {
      return null
    }
  }

  showToolResult(result, toolKey) {
    result = result || {}
    const isSuccess = result.success !== false
    const meta = this.toolMeta(toolKey)
    const executionId = Number.parseInt(result.execution_id, 10)
    const existing = Number.isFinite(executionId)
      ? this.messagesTarget.querySelector(`[data-execution-id="${executionId}"]`)
      : null

    // Quiet successful inspection steps to a thin muted line.
    if (isSuccess && meta.kind === "read") {
      const html = `
        <div class="max-w-[90%] mr-auto px-1 py-0.5 text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
          <span class="inline-block w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600"></span>
          ${this.escapeHtml(meta.label)}
        </div>`
      if (existing) existing.outerHTML = html
      else this.messagesTarget.insertAdjacentHTML("beforeend", html)
      this.scrollToBottom()
      return
    }

    const html = this.toolActionHtml({
      label: meta.label,
      success: isSuccess,
      message: isSuccess ? result.message : (result.error || result.message),
      editUrl: result.data?.edit_url
    })
    if (existing) existing.outerHTML = html
    else this.messagesTarget.insertAdjacentHTML("beforeend", html)
    this.scrollToBottom()
  }

  // Compact action row for a completed tool call (live results and history).
  toolActionHtml({ label, success = true, message = null, editUrl = null }) {
    const iconColor = success ? "text-teal-500" : "text-red-500"
    const path = success ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"
    const icon = `<svg class="w-4 h-4 ${iconColor} shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="${path}"/></svg>`
    const sub = message && this.escapeHtml(message) !== this.escapeHtml(label)
      ? `<div class="text-xs text-slate-500 dark:text-slate-400 break-words">${this.escapeHtml(message)}</div>`
      : ""
    const safeEditUrl = this.safeInternalUrl(editUrl)
    const link = safeEditUrl
      ? `<a href="${safeEditUrl}" class="text-xs text-teal-600 dark:text-teal-400 underline hover:no-underline">${this.escapeHtml(this.message("view_edit", "View / edit"))}</a>`
      : ""
    return `
      <div class="max-w-[90%] mr-auto my-1 flex items-start gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 min-w-0">
        ${icon}
        <div class="min-w-0">
          <div class="text-sm font-medium text-slate-700 dark:text-slate-200 break-words">${this.escapeHtml(label)}</div>
          ${sub}
          ${link}
        </div>
      </div>`
  }

  appendExecutionsHistory(executions) {
    executions.forEach((execution) => {
      if (execution.status === "pending") {
        this.showToolConfirmation({
          execution_id: execution.id,
          tool_key: execution.tool_key,
          tool_name: execution.tool_name,
          params: execution.params
        })
        return
      }

      if (execution.status === "running") {
        this.messagesTarget.insertAdjacentHTML("beforeend", `
          <div class="max-w-[90%] mr-auto my-1 rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-sm text-slate-500 dark:text-slate-400"
               data-execution-id="${execution.id}">
            ${this.escapeHtml(execution.tool_name)} — executing…
          </div>`)
        return
      }

      const output = { ...(execution.output || {}), execution_id: execution.id }
      if ([ "failed", "rejected", "skipped" ].includes(execution.status)) {
        output.success = false
        output.error ||= execution.error || (execution.status === "rejected" ? this.message("rejected", "Rejected") : this.message("action_failed", "Action failed"))
      }
      this.showToolResult(output, execution.tool_key)
    })
    this.scrollToBottom()
  }

  formatToolName(name) {
    return String(name || this.message("action", "Action"))
      .replace(/^builder_/, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
  }

  showError(message) {
    const html = `
      <div class="max-w-[90%] mr-auto my-1 flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20 px-2.5 py-1.5">
        <svg class="w-4 h-4 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
        <div class="text-sm text-red-700 dark:text-red-300 break-words">${this.escapeHtml(this.friendlyError(message))}</div>
      </div>`
    this.messagesTarget.insertAdjacentHTML("beforeend", html)
    this.scrollToBottom()
  }

  // Turn raw provider/thread errors into a clear, actionable message.
  friendlyError(message) {
    const m = String(message ?? "")
    if (/No tool output found|function_call_output|previous_response/i.test(m)) {
      return this.message("interrupted", "The previous step was interrupted, so I reset the session. Please send your last request again and I'll continue.")
    }
    if (/rate.?limit|429/i.test(m)) return this.message("rate_limited", "Rate limit reached — please wait a moment and try again.")
    if (/temperature|unsupported parameter/i.test(m)) return this.message("bad_parameter", "The AI model rejected a request parameter. This is a configuration issue — please report it.")
    const api = m.match(/API Error\s*\(?\d*\)?:\s*(.+)/i)
    if (api) return this.message("api_error", "The AI service returned an error: %{error}", { error: api[1] })
    return m
  }

  showTypingIndicator() {
    if (this.hasTypingIndicatorTarget) {
      this.typingIndicatorTarget.classList.remove("hidden")
    }
    this.scrollToBottom()
  }

  hideTypingIndicator() {
    if (this.hasTypingIndicatorTarget) {
      this.typingIndicatorTarget.classList.add("hidden")
    }
  }

  disableSend() {
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = true
    }
    if (this.hasInputTarget) {
      this.inputTarget.disabled = true
    }
    this.updateRunControls()
  }

  enableSend() {
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = false
    }
    if (this.hasInputTarget) {
      this.inputTarget.disabled = false
      this.inputTarget.focus()
    }
    this.updateRunControls()
  }

  // While a run is active the Send button gives way to Stop.
  updateRunControls() {
    if (!this.hasStopButtonTarget) return

    const running = !!this.activeRunId
    this.stopButtonTarget.classList.toggle("hidden", !running)
    if (this.hasSendButtonTarget) this.sendButtonTarget.classList.toggle("hidden", running)
  }

  async stopRun() {
    const runId = this.activeRunId
    if (!runId || !this.conversationIdValue) return

    this.stopButtonTarget.disabled = true
    this.stoppedRunIds.add(runId)

    try {
      const response = await fetch(`/seller/ai/conversations/${this.conversationIdValue}/cancel`, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken }
      })
      if (!response.ok) throw new Error(`Cancel status ${response.status}`)

      const data = await response.json()
      if (data.cancelled_run_ids?.includes(runId)) {
        this.finishStoppedRun(runId)
      } else {
        // The run finished on its own just before the stop landed; its final
        // events were dropped above, so take the outcome from history.
        this.stoppedRunIds.delete(runId)
        this.loadHistory()
      }
    } catch (_error) {
      this.stoppedRunIds.delete(runId)
      this.showError(this.message("stop_failed", "Couldn’t stop the AI. Please try again."))
    } finally {
      this.stopButtonTarget.disabled = false
    }
  }

  // Keeps whatever was streamed so far, retires unanswered confirmation cards
  // and unlocks the input. Runs once per run, whichever tab stopped it.
  finishStoppedRun(runId) {
    this.stoppedRunIds.add(runId)
    if (this.activeRunId !== runId) return

    this.stopRunPolling()
    this.clearResponseTimeout()
    this.activeRunId = null
    this.finalizeResponse()
    this.hideTypingIndicator()
    this.resetSequencing()
    this.messagesTarget.querySelectorAll("[data-execution-id] button").forEach((button) => { button.disabled = true })
    this.showStoppedNotice()
    this.enableSend()
    this.notifyBuilderPreview()
  }

  showStoppedNotice() {
    const html = `<div class="mr-auto my-1 text-xs text-slate-400 dark:text-slate-500">${this.escapeHtml(this.message("stopped", "Stopped. Changes made before stopping are kept."))}</div>`
    this.messagesTarget.insertAdjacentHTML("beforeend", html)
    this.scrollToBottom()
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    }
  }

  buildMessageHtml(role, content, attachments = [], citations = []) {
    const attachmentHtml = attachments.length > 0
      ? `<div class="flex flex-wrap gap-1 mb-2">${attachments.map(a =>
          a.contentType?.startsWith("image/")
            ? `<img src="${this.escapeHtml(a.previewUrl || a.url)}" class="w-16 h-16 object-cover rounded" />`
            : `<div class="px-2 py-1 bg-white/20 rounded text-xs flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                ${this.escapeHtml(a.filename)}
              </div>`
        ).join("")}</div>`
      : ""

    if (role === "user") {
      return `
        <div class="max-w-[90%] min-w-0 ml-auto">
          <div class="bg-teal-600 text-white rounded-2xl rounded-br-md px-4 py-2 text-sm leading-relaxed break-words overflow-hidden">
            ${attachmentHtml}
            ${this.formatMarkdownWithCitations(content, citations)}
          </div>
        </div>
      `
    } else {
      return `
        <div class="max-w-[90%] min-w-0 mr-auto">
          <div class="bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white rounded-2xl rounded-bl-md px-4 py-2 text-sm leading-relaxed break-words overflow-hidden">
            ${attachmentHtml}
            ${this.formatMarkdownWithCitations(content, citations)}
          </div>
        </div>
      `
    }
  }

  formatMarkdown(text) {
    if (!text) return ""

    return this.escapeHtml(text)
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/`([^`]+)`/g, '<code class="break-all rounded bg-black/10 px-1 py-0.5 text-[0.85em]">$1</code>')
      .replace(/\n/g, "<br>")
  }

  formatMarkdownWithCitations(text, citations) {
    const safe = this.safeCitations(citations)
      .filter((citation) => Number.isInteger(citation.end_index) && citation.end_index >= 0)
      .sort((left, right) => left.end_index - right.end_index)
    if (safe.length === 0) return this.formatMarkdown(text)

    let cursor = 0
    let html = ""
    safe.forEach((citation, index) => {
      const endIndex = Math.min(citation.end_index, text.length)
      if (endIndex > cursor) {
        html += this.formatMarkdown(text.slice(cursor, endIndex))
        cursor = endIndex
      }
      html += `<sup><a href="${this.escapeHtml(citation.url)}" target="_blank" rel="noopener noreferrer" class="ml-0.5 text-teal-600 dark:text-teal-300 hover:underline" title="${this.escapeHtml(citation.title || citation.url)}">[${index + 1}]</a></sup>`
    })
    html += this.formatMarkdown(text.slice(cursor))
    return html
  }

  formatParams(params) {
    return this.escapeHtml(JSON.stringify(params, null, 2))
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage()
    }
  }

  // Auto-resize textarea as user types
  autoResize(event) {
    const textarea = event.target
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = "auto"
    // Set to scrollHeight, but respect max-height from CSS
    const maxHeight = 150
    const newHeight = Math.min(textarea.scrollHeight, maxHeight)
    textarea.style.height = `${newHeight}px`
  }

  // Reset textarea height after sending
  resetInputHeight() {
    if (this.hasInputTarget) {
      this.inputTarget.style.height = "auto"
    }
  }

  async loadHistory() {
    try {
      const response = await fetch(`/seller/ai/conversations/${this.conversationIdValue}`)
      if (!response.ok) return

      const data = await response.json()
      this.renderHistory(data)
      this.syncActiveRun(data.active_run)
    } catch (error) {
      console.error("Error loading history:", error)
    }
  }

  renderHistory(data) {
    this.finalizeResponse()

    // Replace the placeholder / any prior render with the restored history.
    if (data.messages.length > 0) this.messagesTarget.innerHTML = ""

    data.messages.forEach(msg => {
      const attachments = (msg.attachments || []).map(attachment => ({
        signedId: attachment.signed_id,
        filename: attachment.filename,
        contentType: attachment.content_type,
        url: attachment.url
      }))

      // Skip system and tool messages - they're internal to the LLM conversation
      if (msg.role === "user") {
        this.appendMessage(msg.role, msg.content, attachments)
      } else if (msg.role === "assistant") {
        // Show content if present
        if (msg.content) {
          this.appendMessage(msg.role, msg.content, [], msg.citations || [])
        }
        if (msg.executions && msg.executions.length > 0) {
          this.appendExecutionsHistory(msg.executions)
        }
        if (msg.citations && msg.citations.length > 0) {
          this.showCitations(msg.citations)
        }
      }
    })
  }

  syncActiveRun(activeRun) {
    if (activeRun) {
      this.activeRunId = activeRun.id
      if (activeRun.status === "waiting_confirmation") this.hideTypingIndicator()
      else this.showTypingIndicator()
      this.disableSend()
      this.startRunPolling(activeRun.status === "waiting_confirmation" ? 5000 : 2000)
      return
    }

    const wasRunning = !!this.activeRunId
    this.activeRunId = null
    this.stopRunPolling()
    this.clearResponseTimeout()
    this.hideTypingIndicator()
    this.enableSend()
    if (wasRunning) this.notifyBuilderPreview()
  }

  startRunPolling(delay = 2000) {
    if (this.runPollTimer || !this.activeRunId) return

    this.runPollTimer = setTimeout(() => this.pollActiveRun(), delay)
  }

  stopRunPolling() {
    if (!this.runPollTimer) return

    clearTimeout(this.runPollTimer)
    this.runPollTimer = null
  }

  async pollActiveRun() {
    this.runPollTimer = null
    if (!this.activeRunId || !this.conversationIdValue) return

    try {
      const response = await fetch(`/seller/ai/conversations/${this.conversationIdValue}`, {
        headers: { "Accept": "application/json" }
      })
      if (!response.ok) throw new Error(`Conversation status ${response.status}`)

      const data = await response.json()
      if (data.active_run?.id === this.activeRunId) {
        this.startRunPolling(data.active_run.status === "waiting_confirmation" ? 5000 : 2000)
      } else {
        // A full reload can miss the final cable events. Persisted history is
        // authoritative, so redraw once when the active run finishes.
        this.renderHistory(data)
        this.syncActiveRun(data.active_run)
      }
    } catch (_error) {
      // Do not mark the run failed because one status request failed. Cable may
      // still be connected; keep checking while this run remains active.
      this.startRunPolling()
    }
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  get welcomeMessage() {
    return this.globalWelcomeValue
  }

  get hasPageContext() {
    return !!this.contextTypeValue && this.contextIdValue > 0
  }

  get conversationListUrl() {
    const query = new URLSearchParams({ agent_type: this.agentTypeValue })
    return `/seller/ai/conversations?${query.toString()}`
  }

  scheduleLiveContextRefresh(result) {
    if (!result || result.success === false || !result.record_type) return

    const container = document.getElementById("ai-live-context")
    if (!container) return
    if (container.dataset.aiLiveRecordType !== String(result.record_type)) return
    // A record page refreshes for its own record; a list page (no id) for any
    // record of its type.
    const recordId = container.dataset.aiLiveRecordId
    if (recordId && Number(recordId) !== Number(result.record_id)) return

    if (this.liveRefreshTimer) clearTimeout(this.liveRefreshTimer)
    this.showLiveStatus(this.updatingTextValue)
    this.liveRefreshTimer = setTimeout(() => this.refreshLiveContext(), 200)
  }

  async refreshLiveContext() {
    this.liveRefreshTimer = null
    const current = document.getElementById("ai-live-context")
    if (!current) return

    try {
      const response = await fetch(window.location.href, {
        headers: { "Accept": "text/html", "X-AI-Live-Refresh": "1" }
      })
      if (!response.ok) throw new Error(`Page refresh failed (${response.status})`)

      const documentCopy = new DOMParser().parseFromString(await response.text(), "text/html")
      const updated = documentCopy.getElementById("ai-live-context")
      if (!updated) throw new Error("Updated page context was not found")

      current.replaceWith(document.importNode(updated, true))
      if (documentCopy.title) document.title = documentCopy.title
      this.showLiveStatus(this.updatedTextValue, 2200)
      window.dispatchEvent(new CustomEvent("ai:context-refreshed", {
        detail: { recordType: updated.dataset.aiLiveRecordType, recordId: Number(updated.dataset.aiLiveRecordId) }
      }))
    } catch (error) {
      console.error("AI live context refresh failed:", error)
      this.showLiveStatus(this.refreshFailedTextValue, 5000, true)
    }
  }

  showLiveStatus(message, hideAfter = null, error = false) {
    if (!this.hasLiveStatusTarget) return

    this.liveStatusTarget.textContent = message
    this.liveStatusTarget.classList.remove("hidden", "text-teal-600", "dark:text-teal-400", "text-red-600", "dark:text-red-400")
    this.liveStatusTarget.classList.add(error ? "text-red-600" : "text-teal-600")
    this.liveStatusTarget.classList.add(error ? "dark:text-red-400" : "dark:text-teal-400")
    if (hideAfter) setTimeout(() => this.liveStatusTarget.classList.add("hidden"), hideAfter)
  }

  // Toggle conversation list visibility
  async toggleConversationList() {
    if (!this.hasConversationListTarget) return

    const isHidden = this.conversationListTarget.classList.contains("hidden")

    if (isHidden) {
      await this.loadConversations()
      this.conversationListTarget.classList.remove("hidden")
    } else {
      this.conversationListTarget.classList.add("hidden")
    }
  }

  // Load conversations from server
  async loadConversations() {
    if (!this.hasConversationItemsTarget) return

    try {
      const response = await fetch(this.conversationListUrl, {
        headers: { "Accept": "application/json" }
      })

      if (!response.ok) return

      const conversations = await response.json()
      this.renderConversationList(conversations)
    } catch (error) {
      console.error("Error loading conversations:", error)
    }
  }

  // Render conversation list
  renderConversationList(conversations) {
    if (!this.hasConversationItemsTarget) return

    if (conversations.length === 0) {
      this.conversationItemsTarget.innerHTML = `
        <div class="text-sm text-slate-500 dark:text-slate-400 text-center py-2">
          No conversations yet
        </div>
      `
      return
    }

    const html = conversations.map(conv => `
      <button type="button"
              class="w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                conv.id === this.conversationIdValue
                  ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                  : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
              }"
              data-action="ai-sidebar#switchConversation"
              data-conversation-id="${conv.id}">
        <div class="font-medium truncate">${this.escapeHtml(conv.preview)}</div>
        <div class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          ${this.formatTimeAgo(conv.updated_at)}
        </div>
      </button>
    `).join("")

    this.conversationItemsTarget.innerHTML = html
  }

  // Switch to a different conversation
  async switchConversation(event) {
    const conversationId = parseInt(event.currentTarget.dataset.conversationId)
    if (conversationId === this.conversationIdValue) {
      this.conversationListTarget.classList.add("hidden")
      return
    }

    // Clear current messages and reset state
    if (this.hasMessagesTarget) {
      this.messagesTarget.innerHTML = ""
    }
    this.resetSequencing()

    // Update conversation ID and subscribe
    this.conversationIdValue = conversationId
    this.pendingMessageRequest = null
    this.subscribeToConversation(conversationId)

    // Load history for this conversation
    await this.loadHistory()

    // Hide conversation list
    if (this.hasConversationListTarget) {
      this.conversationListTarget.classList.add("hidden")
    }
  }

  // Start a new conversation
  newConversation() {
    if (this.activeRunId) {
      this.showError(this.message("busy", "Wait for the current AI request to finish before starting a new conversation."))
      return
    }

    // Keep the UI blank until the first message; sendMessage creates the
    // persisted conversation lazily.
    this.startFreshConversation()
  }

  // Format time ago helper
  formatTimeAgo(dateString) {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return this.message("just_now", "just now")
    if (diffMins < 60) return this.message("minutes_ago", "%{count}m ago", { count: diffMins })
    if (diffHours < 24) return this.message("hours_ago", "%{count}h ago", { count: diffHours })
    if (diffDays < 7) return this.message("days_ago", "%{count}d ago", { count: diffDays })
    return date.toLocaleDateString(document.documentElement.lang || undefined)
  }

  // =====================
  // Attachment Methods
  // =====================

  selectAttachments() {
    if (this.hasAttachmentInputTarget) {
      this.attachmentInputTarget.click()
    }
  }

  useSuggestion(event) {
    if (!this.hasInputTarget) return
    this.inputTarget.value = event.currentTarget.dataset.suggestion || ""
    this.autoResize({ target: this.inputTarget })
    this.inputTarget.focus()
  }

  async handleAttachmentSelect(event) {
    const files = Array.from(event.target.files)
    if (files.length === 0) return

    for (const file of files) {
      if (this.pendingAttachments.length >= MAX_ATTACHMENTS) {
        this.showError(this.message("too_many_files", "You can attach up to %{count} images", { count: MAX_ATTACHMENTS }))
        break
      }

      if (!["image/jpeg", "image/png", "image/gif", "image/webp"].includes(file.type)) {
        this.showError(this.message("unsupported_file", "File \"%{name}\" is not a supported image (JPEG, PNG, GIF, or WebP)", { name: file.name }))
        continue
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        this.showError(this.message("file_too_large", "File \"%{name}\" is too large (max 10MB)", { name: file.name }))
        continue
      }

      // Show loading placeholder immediately
      const placeholderId = this.showUploadingPlaceholder(file)

      try {
        await this.uploadAttachment(file, placeholderId)
      } catch (error) {
        // Remove placeholder on error
        this.removeUploadingPlaceholder(placeholderId)
      }
    }

    // Clear input for re-selection
    event.target.value = ""
  }

  showUploadingPlaceholder(file) {
    if (!this.hasAttachmentPreviewTarget) return null

    this.attachmentPreviewTarget.classList.remove("hidden")

    const placeholderId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    // Show image preview with loading overlay, or spinner for non-images
    const isImage = file.type.startsWith("image/")
    const previewUrl = isImage ? URL.createObjectURL(file) : null

    const placeholderHtml = `
      <div class="relative" data-upload-id="${placeholderId}">
        ${isImage
          ? `<img src="${previewUrl}" class="w-10 h-10 object-cover rounded-lg opacity-50" />`
          : `<div class="w-10 h-10 bg-slate-200 dark:bg-slate-600 rounded-lg"></div>`
        }
        <div class="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
          <svg class="animate-spin w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    `
    this.attachmentPreviewTarget.insertAdjacentHTML("beforeend", placeholderHtml)

    return placeholderId
  }

  removeUploadingPlaceholder(placeholderId) {
    if (!placeholderId) return
    const placeholder = this.attachmentPreviewTarget?.querySelector(`[data-upload-id="${placeholderId}"]`)
    this.revokeObjectUrl(placeholder?.querySelector("img")?.src)
    placeholder?.remove()

    // Hide preview area if empty
    if (this.pendingAttachments.length === 0 && this.hasAttachmentPreviewTarget) {
      const hasContent = this.attachmentPreviewTarget.children.length > 0
      if (!hasContent) {
        this.attachmentPreviewTarget.classList.add("hidden")
      }
    }
  }

  async uploadAttachment(file, placeholderId) {
    try {
      const blob = await this.uploadFile(file)
      const placeholder = this.attachmentPreviewTarget?.querySelector(`[data-upload-id="${placeholderId}"]`)
      const existingPreviewUrl = placeholder?.querySelector("img")?.src
      const attachment = {
        file,
        signedId: blob.signed_id,
        previewUrl: existingPreviewUrl || URL.createObjectURL(file),
        contentType: file.type,
        filename: file.name
      }
      this.pendingAttachments.push(attachment)

      // Replace placeholder with final preview
      this.replaceUploadingPlaceholder(placeholderId, attachment)
      return blob
    } catch (error) {
      console.error("Upload error:", error)
      this.showError(error.message || this.message("upload_failed", "Failed to upload %{name}", { name: file.name }))
      throw error
    }
  }

  async uploadFile(file) {
    const body = new FormData()
    body.append("file", file)

    const response = await fetch("/seller/ai/direct_uploads", {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      body
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(data.error || this.message("upload_failed", "Failed to upload %{name}", { name: file.name }))
    }

    return data
  }

  replaceUploadingPlaceholder(placeholderId, attachment) {
    if (!this.hasAttachmentPreviewTarget) return

    const placeholder = this.attachmentPreviewTarget.querySelector(`[data-upload-id="${placeholderId}"]`)

    const previewHtml = attachment.contentType.startsWith("image/")
      ? `<img src="${this.escapeHtml(attachment.previewUrl)}" class="w-10 h-10 object-cover rounded-lg" />`
      : `<div class="w-10 h-10 bg-slate-200 dark:bg-slate-600 rounded-lg flex items-center justify-center">
          <svg class="w-4 h-4 text-slate-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>`

    const itemHtml = `
      <div class="relative group" data-signed-id="${this.escapeHtml(attachment.signedId)}">
        ${previewHtml}
        <button type="button"
                class="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                data-action="click->ai-sidebar#removeAttachment"
                data-signed-id="${this.escapeHtml(attachment.signedId)}">&times;</button>
      </div>
    `

    if (placeholder) {
      placeholder.outerHTML = itemHtml
    } else {
      // Fallback if placeholder not found
      this.attachmentPreviewTarget.insertAdjacentHTML("beforeend", itemHtml)
    }
  }

  renderAttachmentPreview(attachment) {
    if (!this.hasAttachmentPreviewTarget) return

    this.attachmentPreviewTarget.classList.remove("hidden")

    const previewHtml = attachment.contentType.startsWith("image/")
      ? `<img src="${this.escapeHtml(attachment.previewUrl)}" class="w-10 h-10 object-cover rounded-lg" />`
      : `<div class="w-10 h-10 bg-slate-200 dark:bg-slate-600 rounded-lg flex items-center justify-center">
          <svg class="w-4 h-4 text-slate-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>`

    const itemHtml = `
      <div class="relative group" data-signed-id="${this.escapeHtml(attachment.signedId)}">
        ${previewHtml}
        <button type="button"
                class="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                data-action="click->ai-sidebar#removeAttachment"
                data-signed-id="${this.escapeHtml(attachment.signedId)}">&times;</button>
      </div>
    `
    this.attachmentPreviewTarget.insertAdjacentHTML("beforeend", itemHtml)
  }

  removeAttachment(event) {
    const signedId = event.currentTarget.dataset.signedId
    const removed = this.pendingAttachments.find(a => a.signedId === signedId)
    this.pendingAttachments = this.pendingAttachments.filter(a => a.signedId !== signedId)
    this.revokeObjectUrl(removed?.previewUrl)
    event.currentTarget.closest("[data-signed-id]").remove()

    if (this.pendingAttachments.length === 0 && this.hasAttachmentPreviewTarget) {
      this.attachmentPreviewTarget.classList.add("hidden")
    }
  }

  clearAttachmentPreviews() {
    const previewUrls = this.pendingAttachments.map(attachment => attachment.previewUrl)
    this.pendingAttachments = []
    if (this.hasAttachmentPreviewTarget) {
      this.attachmentPreviewTarget.innerHTML = ""
      this.attachmentPreviewTarget.classList.add("hidden")
    }
    // A sent message may still be decoding the local preview. Give the browser
    // a moment before releasing the backing object URLs.
    setTimeout(() => previewUrls.forEach((url) => this.revokeObjectUrl(url)), 1_000)
  }

  revokeObjectUrl(url) {
    if (String(url || "").startsWith("blob:")) URL.revokeObjectURL(url)
  }

  // =====================
  // Voice Recording Methods
  // =====================

  async toggleVoiceRecording() {
    if (this.isRecording) {
      this.stopRecording()
    } else {
      await this.startRecording()
    }
  }

  async startRecording() {
    // Check browser support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.showError(this.message("voice_unsupported", "Voice recording is not supported in this browser. Please use Chrome, Firefox, or Edge."))
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      // Try to create MediaRecorder with supported mimeType
      const mimeType = this.getSupportedMimeType()
      const options = mimeType ? { mimeType } : {}
      this.mediaRecorder = new MediaRecorder(stream, options)

      this.audioChunks = []

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data)
        }
      }

      this.mediaRecorder.onstop = () => {
        this.processRecording()
        stream.getTracks().forEach(track => track.stop())
      }

      this.mediaRecorder.start()
      this.isRecording = true
      this.showRecordingIndicator()
      this.updateVoiceButtonState()

    } catch (error) {
      console.error("Microphone error:", error)

      if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
        this.showError(this.message("mic_denied", "Microphone access denied. Please allow microphone access in your browser settings."))
      } else if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
        this.showError(this.message("mic_missing", "No microphone found. Please connect a microphone and try again."))
      } else if (error.name === "NotReadableError" || error.name === "TrackStartError") {
        this.showError(this.message("mic_busy", "Microphone is in use by another application."))
      } else if (error.name === "OverconstrainedError") {
        this.showError(this.message("mic_config", "Microphone configuration error. Please try again."))
      } else if (error.name === "SecurityError") {
        this.showError(this.message("mic_https", "Microphone access requires HTTPS. Please use a secure connection."))
      } else {
        this.showError(this.message("mic_error", "Microphone error: %{error}", { error: error.message || this.message("unknown_error", "Unknown error") }))
      }
    }
  }

  stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop()
    }
    this.isRecording = false
    this.hideRecordingIndicator()
    this.updateVoiceButtonState()
  }

  getSupportedMimeType() {
    if (typeof MediaRecorder === "undefined") {
      return null
    }

    const types = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/ogg",
      "audio/wav"
    ]
    return types.find(type => MediaRecorder.isTypeSupported(type)) || null
  }

  async processRecording() {
    if (this.audioChunks.length === 0) return

    const mimeType = this.mediaRecorder?.mimeType || this.getSupportedMimeType() || "audio/webm"
    const audioBlob = new Blob(this.audioChunks, { type: mimeType })

    // Show processing state
    this.showTypingIndicator()
    if (this.hasInputTarget) {
      this.defaultPlaceholder ??= this.inputTarget.placeholder
      this.inputTarget.placeholder = this.message("transcribing", "Transcribing...")
      this.inputTarget.disabled = true
    }

    try {
      // Upload audio to get signed ID
      const signedId = await this.uploadAudio(audioBlob)

      // Send to transcription endpoint
      const response = await fetch("/seller/ai/transcriptions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ audio_signed_id: signedId })
      })

      const data = await response.json()

      if (response.ok) {
        // Insert transcribed text into input
        if (this.hasInputTarget) {
          this.inputTarget.value = data.text
          this.inputTarget.focus()
        }
      } else {
        this.showError(data.error || this.message("transcription_failed", "Transcription failed"))
      }
    } catch (error) {
      console.error("Transcription error:", error)
      this.showError(this.message("transcription_failed", "Transcription failed"))
    } finally {
      this.hideTypingIndicator()
      if (this.hasInputTarget) {
        this.inputTarget.placeholder = this.defaultPlaceholder || this.message("placeholder", "Type your message...")
        this.inputTarget.disabled = false
      }
    }
  }

  async uploadAudio(audioBlob) {
    // Create a File object with proper extension
    const normalizedType = this.normalizeAudioMimeType(audioBlob.type)
    const extension = this.extensionForAudioType(normalizedType)
    const file = new File([audioBlob], `recording.${extension}`, { type: normalizedType })
    const blob = await this.uploadFile(file)
    return blob.signed_id
  }

  normalizeAudioMimeType(mimeType) {
    if (!mimeType) return "audio/webm"

    // Strip codec parameters (e.g., "audio/webm;codecs=opus")
    return mimeType.split(";")[0]
  }

  extensionForAudioType(mimeType) {
    switch (mimeType) {
      case "audio/webm":
        return "webm"
      case "audio/mp4":
        return "m4a"
      case "audio/ogg":
        return "ogg"
      case "audio/wav":
        return "wav"
      default:
        return "webm"
    }
  }

  showRecordingIndicator() {
    if (this.hasRecordingIndicatorTarget) {
      this.recordingIndicatorTarget.classList.remove("hidden")
    }
  }

  hideRecordingIndicator() {
    if (this.hasRecordingIndicatorTarget) {
      this.recordingIndicatorTarget.classList.add("hidden")
    }
  }

  updateVoiceButtonState() {
    if (!this.hasVoiceButtonTarget) return

    if (this.isRecording) {
      this.voiceButtonTarget.classList.add("bg-red-500", "text-white")
      this.voiceButtonTarget.classList.remove("text-slate-500", "hover:text-teal-600", "dark:text-slate-400", "dark:hover:text-teal-400")
    } else {
      this.voiceButtonTarget.classList.remove("bg-red-500", "text-white")
      this.voiceButtonTarget.classList.add("text-slate-500", "hover:text-teal-600", "dark:text-slate-400", "dark:hover:text-teal-400")
    }
  }
}
